import { Anchor, Badge, Group, Image, Stack, Text, Title } from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import DOMPurify from "dompurify";
import { ManifestSummary } from "./ManifestSummary";
import type { App } from "./types";
import { formatDate, isSafeUrl } from "./types";

function SafeLink({ href, label }: { href: string; label: string }) {
  if (!isSafeUrl(href)) {
    return null;
  }

  return (
    <Anchor href={href} target="_blank" rel="noopener noreferrer" size="sm">
      <Group gap={4}>
        <IconExternalLink size={14} />
        {label}
      </Group>
    </Anchor>
  );
}

export function OverviewTab({ app }: { app: App }) {
  return (
    <Stack gap="xl">
      {/* Description */}
      {app.description && (
        <Stack gap="xs">
          <Title order={5}>Description</Title>
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(app.description),
            }}
          />
        </Stack>
      )}

      {/* Screenshots */}
      {app.screenshots.length > 0 && (
        <Stack gap="xs">
          <Title order={5}>Screenshots</Title>
          <Group gap="sm">
            {app.screenshots.map((url) => (
              <Image
                key={url}
                src={url}
                width={200}
                height={120}
                radius="sm"
                fit="cover"
              />
            ))}
          </Group>
        </Stack>
      )}

      {/* External Links */}
      {(app.websiteUrl || app.supportUrl || app.privacyPolicyUrl) && (
        <Stack gap="xs">
          <Title order={5}>Links</Title>
          <Group gap="md">
            {app.websiteUrl && (
              <SafeLink href={app.websiteUrl} label="Website" />
            )}
            {app.supportUrl && (
              <SafeLink href={app.supportUrl} label="Support" />
            )}
            {app.privacyPolicyUrl && (
              <SafeLink href={app.privacyPolicyUrl} label="Privacy Policy" />
            )}
          </Group>
        </Stack>
      )}

      {/* Categories */}
      {app.categories.length > 0 && (
        <Stack gap="xs">
          <Title order={5}>Categories</Title>
          <Group gap={6}>
            {app.categories.map((cat) => (
              <Badge key={cat} variant="outline" size="sm">
                {cat}
              </Badge>
            ))}
          </Group>
        </Stack>
      )}

      {/* Timestamps */}
      <Group gap="xl">
        <Text size="xs" c="dimmed">
          Created: {formatDate(app.createdAt)}
        </Text>
        <Text size="xs" c="dimmed">
          Updated: {formatDate(app.updatedAt)}
        </Text>
      </Group>

      {/* Manifest Summary */}
      <Title order={5}>Manifest</Title>
      <Text size="sm" c="dimmed">
        The manifest defines your app's capabilities. Update via the CLI or by
        publishing a new release.
      </Text>

      <ManifestSummary manifest={app.manifest} showActivePages />
    </Stack>
  );
}
